import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, Heart, Shield, Leaf } from 'lucide-react';
export function BonusSection() {
  const benefits = [{
    title: 'Los ingredientes básicos',
    desc: 'Aceites, hierbas, vendas y objetos simples explicados uno por uno.'
  }, {
    title: 'Para qué sirve cada cosa',
    desc: 'Qué problema resuelve cada ingrediente y cuándo usarlo.'
  }, {
    title: 'Qué NO comprar',
    desc: 'Ahorro directo: menos productos modernos, menos gasto.'
  }, {
    title: 'Problemas cotidianos resueltos',
    desc: 'Dolores, malestares, limpieza, golpes y resfríos.'
  }, {
    title: 'Mentalidad abuela',
    desc: 'Cómo pensar antes de correr a comprar algo.'
  }];
  const emotions = [{
    icon: Shield,
    label: 'Tranquilidad',
    quote: 'Estoy preparada.',
    color: 'text-[#9CAF88]'
  }, {
    icon: Sparkles,
    label: 'Confianza',
    quote: 'Con poco alcanza.',
    color: 'text-[#D4A5A5]'
  }, {
    icon: Heart,
    label: 'Conexión',
    quote: 'Esto ya funcionaba antes.',
    color: 'text-[#5C4033]'
  }];
  return <section className="py-24 bg-[#F9F4EF] relative overflow-hidden" data-id="element-3">
      {/* Decorative vintage background elements */}
      <div className="absolute top-0 left-0 w-full h-4 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] opacity-50" data-id="element-4"></div>
      <div className="absolute bottom-0 left-0 w-full h-4 bg-[url('https://www.transparenttextures.com/patterns/paper.png')] opacity-50 rotate-180" data-id="element-5"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-6">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="bg-white rounded-3xl shadow-xl overflow-hidden border border-[#D4A5A5]/30" data-id="element-7">
          <div className="grid lg:grid-cols-12 gap-0" data-id="element-8">
            {/* Image Side - 40% */}
            <div className="lg:col-span-5 bg-[#5C4033] relative overflow-hidden flex items-center justify-center p-8 lg:p-12" data-id="element-9">
              <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" data-id="element-10"></div>
              <motion.div whileHover={{
              scale: 1.02
            }} transition={{
              duration: 0.5
            }} className="relative z-10 w-full max-w-md mx-auto" data-id="element-11">
                <img src="https://cdn.magicpatterns.com/uploads/rJeNMzg8dxsdJyeTSiSTAJ/ChatGPT_Image_21_dic_2025,_06_09_59_p.m..png" alt="El Botiquín Invisible de la Abuela - Guía de remedios naturales" className="w-full h-auto rounded-lg shadow-2xl border-4 border-[#FFF8F0]/20" data-id="element-12" />
                {/* Vintage badge overlay */}
                <div className="absolute -top-4 -right-4 bg-[#D4A5A5] text-white w-24 h-24 rounded-full flex items-center justify-center text-center p-2 shadow-lg transform rotate-12 border-4 border-white" data-id="element-13">
                  <span className="font-serif font-bold leading-tight text-sm" data-id="element-14">
                    Valor
                    <br data-id="element-15" />
                    <span className="text-lg" data-id="element-16">$15.000</span>
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Content Side - 60% */}
            <div className="lg:col-span-7 p-8 lg:p-12 flex flex-col justify-center bg-[#FFF8F0]" data-id="element-17">
              <div className="mb-6" data-id="element-18">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#9CAF88]/20 text-[#5C4033] text-sm font-bold tracking-wider uppercase mb-4 border border-[#9CAF88]/30" data-id="element-19">
                  <Sparkles className="w-4 h-4" data-id="element-20" />
                  Bono Exclusivo #1
                </span>
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#5C4033] mb-4 leading-tight" data-id="element-21">
                  El Botiquín Invisible de la Abuela
                </h2>
                <p className="text-xl text-[#8B7355] font-serif italic border-l-4 border-[#D4A5A5] pl-4 py-1" data-id="element-22">
                  "Todo lo que una abuela sabía… y siempre tenía sin que nadie
                  lo note"
                </p>
              </div>

              <p className="text-lg text-[#6B5A4D] mb-8 leading-relaxed" data-id="element-23">
                Una guía práctica y visual inspirada en el botiquín real de
                nuestras abuelas. No es un libro para leer, es un manual para
                usar y resolver el 80% de los problemas cotidianos.
              </p>

              {/* Benefits List */}
              <div className="grid sm:grid-cols-1 gap-4 mb-10" data-id="element-24">
                {benefits.map((benefit, index) => <div key={index} className="flex items-start gap-3" data-id="element-25">
                    <div className="mt-1 bg-[#9CAF88] rounded-full p-1 flex-shrink-0" data-id="element-26">
                      <Check className="w-3 h-3 text-white" data-id="element-27" />
                    </div>
                    <div data-id="element-28">
                      <strong className="text-[#5C4033] font-bold block sm:inline mr-1" data-id="element-29">
                        {benefit.title}:
                      </strong>
                      <span className="text-[#6B5A4D]" data-id="element-30">{benefit.desc}</span>
                    </div>
                  </div>)}
              </div>

              {/* Emotional Benefits Box */}
              <div className="bg-white rounded-xl p-6 border border-[#D4A5A5]/20 shadow-sm mb-8" data-id="element-31">
                <h4 className="text-sm font-bold text-[#8B7355] uppercase tracking-wider mb-4 text-center" data-id="element-32">
                  Lo que vas a sentir
                </h4>
                <div className="grid grid-cols-3 gap-4 text-center divide-x divide-[#D4A5A5]/20" data-id="element-33">
                  {emotions.map((item, idx) => <div key={idx} className="flex flex-col items-center px-2 first:pl-0 last:pr-0" data-id="element-34">
                      <item.icon className={`w-6 h-6 mb-2 ${item.color}`} data-id="element-35" />
                      <span className="font-bold text-[#5C4033] text-sm mb-1 block" data-id="element-36">
                        {item.label}
                      </span>
                      <span className="text-xs text-[#8B7355] italic" data-id="element-37">
                        "{item.quote}"
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="flex items-center gap-3 text-[#5C4033] font-medium bg-[#D4A5A5]/10 p-4 rounded-lg border border-[#D4A5A5]/30" data-id="element-38">
                <Leaf className="w-5 h-5 text-[#9CAF88]" data-id="element-39" />
                <p data-id="element-40">
                  <span className="font-bold" data-id="element-41">GRATIS</span> con la compra del
                  Manual Secreto de la Abuela 2.0.{' '}
                  <span className="text-[#8B7355]" data-id="element-42">Descarga inmediata.</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}